import { useCallback, useEffect, useRef, useState } from 'react'

import {
  fetchInsights,
  markInsightRead,
  replyToInsight,
  type Insight,
} from '../app/api/insightsApi'

const POLL_INTERVAL_MS = 2_000
const MAX_POLL_ATTEMPTS = 30

export function useInsightChat(fingerprint: string) {
  const [insight, setInsight] = useState<Insight | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState('')
  const pollTimer = useRef<number | null>(null)

  const load = useCallback(async () => {
    const insights = await fetchInsights()
    const next = insights.find((item) => item.fingerprint === fingerprint) ?? null
    setInsight(next)
    return next
  }, [fingerprint])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    void load()
      .then((next) => {
        if (cancelled) return
        if (!next) setError('Questa conversazione non è disponibile.')
        else if (!next.readAt) void markInsightRead(next.fingerprint).catch(() => undefined)
      })
      .catch(() => {
        if (!cancelled) setError('Luma non risponde. Riprova.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [load])

  useEffect(() => () => {
    if (pollTimer.current !== null) window.clearTimeout(pollTimer.current)
  }, [])

  const send = useCallback(async (body: string, files: File[] = []) => {
    if (sending || waiting || (body.trim() === '' && files.length === 0)) return false
    setSending(true)
    setError('')
    try {
      const previousAgentReplies = insight?.messages.filter(
        (message) => message.source === 'agent' || message.source === 'voice',
      ).length ?? 0
      const message = await replyToInsight(fingerprint, body.trim(), files)
      setInsight((current) => current ? {
        ...current,
        messages: [...current.messages, message],
      } : current)
      setWaiting(true)

      let attempts = 0
      const poll = async () => {
        try {
          const next = await load()
          const replies = next?.messages.filter(
            (entry) => entry.source === 'agent' || entry.source === 'voice',
          ).length ?? 0
          if (replies > previousAgentReplies || attempts++ >= MAX_POLL_ATTEMPTS) {
            setWaiting(false)
            return
          }
        } catch {
          if (attempts++ >= MAX_POLL_ATTEMPTS) {
            setWaiting(false)
            setError('La risposta sta impiegando più del previsto. Aggiorna la conversazione.')
            return
          }
        }
        pollTimer.current = window.setTimeout(() => { void poll() }, POLL_INTERVAL_MS)
      }
      pollTimer.current = window.setTimeout(() => { void poll() }, POLL_INTERVAL_MS)
      return true
    } catch {
      setError('Il messaggio non è stato inviato. Riprova.')
      return false
    } finally {
      setSending(false)
    }
  }, [fingerprint, insight, load, sending, waiting])

  return {
    insight,
    loading,
    running: sending || waiting,
    error,
    send,
  }
}
